import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import Spinner from "@openup/shared/components/Spinner/Spinner";
import Button from "@openup/shared/components/Button/Button";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import {
  getExternalHealthProviders,
  getFinancialWellbeingExternalHealthProviders,
  getHelpNeededOptions,
  getFinancialWellbeingHelpNeededOptions,
  getNextStepsForSessionNotes,
  SESSION_NOTES_SECTIONS,
} from "@src/utils/constants";
import {
  getTranscriptionSummary,
  useConsultMutation,
  useConsultNotesMutation,
  useDeleteSessionNoteMutation,
} from "@src/queries/consults";
import {
  topicsTagName,
  useConsultTagsQuery,
  useMultipleTagsQueries,
} from "@src/queries/tags";
import {
  getTranslatedThemeNameInSessionLanguage,
  getTranslatedTopicName,
} from "@src/utils/languageHelpers";
import {
  HELP_NEEDED_VALUES,
  PROVIDER_VALUES,
  SESSION_TYPE,
  TAG_TYPES,
} from "@src/constants";
import AttachmentsBox from "@src/views/ClientPage/SessionNotes/components/AttachmentsBox";
import {
  ModelsTag,
  ResponseConsultsConsultWithNotesResponse,
  ResponseThemeResponse,
} from "@src/api/v1/data-contracts";
import { Autocomplete, Checkbox, TextField } from "@mui/material";
import {
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon,
} from "@mui/icons-material";
import EditableMarkdownSection from "@src/views/ClientPage/SessionNotes/components/EditableMarkdownSection";
import { useThemesQuery } from "@src/queries/themes";
import { MDXEditorMethods } from "@mdxeditor/editor";
import {
  withSnackbar,
  WithSnackbarProps,
} from "@src/components/SnackBarComponent";
import SessionNoteRow from "./components/SessionNoteRow";
import EditableDisplay from "./components/EditableDisplay";
import EditableSelectField from "./components/EditableSelectField";
import { EditingContext, NoteSections } from "./EditingContext";

const CLICKABLE_FIELD_CLASS =
  "w-full p-1 body1 text-indigo-800 hover:bg-slate-100 hover:cursor-pointer";

interface ConsultNotesVM {
  consultId: number;
  theme: ResponseThemeResponse | undefined;
  topics: ModelsTag[] | undefined;
  reasonBook: string;
  sessionDiscussed: string;
  helpNeeded: string | null | undefined;
  provider: string | null | undefined;
  providerName: string | null | undefined;
  nextSteps: string | null | undefined;
}
const SessionNotesPaper: React.FC<
  {
    consult: ResponseConsultsConsultWithNotesResponse;
    consultIsLoading: boolean;
  } & WithSnackbarProps
> = ({ consult, consultIsLoading, snackbarShowMessage }) => {
  const { t } = useTranslation();
  const [isTranscriptionSummaryLoading, setIsTranscriptionSummaryLoading] =
    useState(false);

  const { taggedData } = useMultipleTagsQueries([topicsTagName]);

  const allTopicsTags = useMemo(() => taggedData[topicsTagName], [taggedData]);

  const { data: allSessionThemes } = useThemesQuery(
    consult?.consultType ? [consult.consultType] : [],
  );

  const { consultTags: consultTopics, saveTags } = useConsultTagsQuery(
    consult.user.id!,
    consult.id,
    TAG_TYPES.TOPICS,
  );
  const {
    updateConsultTheme: { mutateAsync: updateConsultTheme },
  } = useConsultMutation(consult.user.id!);

  const appInsights = useAppInsightsContext();

  const [selectedTopics, setSelectedTopics] = useState<
    ModelsTag[] | undefined
  >();

  const sortedTopics = useMemo(() => {
    return [...allTopicsTags!.tags!].toSorted((a: ModelsTag, b: ModelsTag) =>
      (
        getTranslatedTopicName(a.name, consult.consultLanguage) ?? ""
      ).localeCompare(
        getTranslatedTopicName(b.name, consult.consultLanguage) ?? "",
      ),
    );
  }, [allTopicsTags, consult.consultLanguage]);

  const notes: ConsultNotesVM = useMemo(
    () => ({
      consultId: consult.id,
      theme: allSessionThemes?.find((theme) => theme.key === consult.themeKey),
      topics: sortedTopics?.filter((topic) =>
        consultTopics?.some((selected) => selected.name === topic.name),
      ),
      reasonBook:
        consult.dossierNotes?.find(
          (n) => n.question === SESSION_NOTES_SECTIONS.REASON_BOOK,
        )?.answer ?? "",
      sessionDiscussed:
        consult.dossierNotes?.find(
          (n) => n.question === SESSION_NOTES_SECTIONS.SESSION_DISCUSSED,
        )?.answer ?? "",
      helpNeeded: consult.dossierNotes?.find(
        (n) => n.question === SESSION_NOTES_SECTIONS.HELP_NEEDED,
      )?.answer,
      provider: consult.dossierNotes?.find(
        (n) => n.question === SESSION_NOTES_SECTIONS.PROVIDER,
      )?.answer,
      providerName: consult.dossierNotes?.find(
        (n) => n.question === SESSION_NOTES_SECTIONS.PROVIDER_NAME,
      )?.answer,
      nextSteps: consult.dossierNotes?.find(
        (n) => n.question === SESSION_NOTES_SECTIONS.NEXT_STEPS,
      )?.answer,
    }),
    [allSessionThemes, consult, consultTopics, sortedTopics],
  );
  useEffect(() => {
    const incoming = notes.topics ?? [];
    // only update state if the contents really changed
    if (
      incoming.length !== selectedTopics?.length ||
      incoming.some((tc, i) => tc !== selectedTopics[i])
    ) {
      setSelectedTopics(incoming);
    }
  }, [notes.topics]);

  const [editingSection, setEditingSection] = useState<
    NoteSections | undefined
  >();
  const emptyState = (
    <span className="text-slate-600">{t("SessionNotesPaper.None")}</span>
  );
  const sessionDiscussedRef = useRef<MDXEditorMethods>(null);

  const loadTranscriptionSummary = async () => {
    setIsTranscriptionSummaryLoading(true);
    const response = await getTranscriptionSummary(consult.id);

    setEditingSection(NoteSections.SESSION_DISCUSSED);

    const matchedTheme = allSessionThemes.filter(
      (theme) =>
        response.theme?.toLocaleUpperCase() === theme.key.toLocaleUpperCase(),
    );

    if (matchedTheme.length > 0) {
      notes.theme = matchedTheme[0];
      await updateConsultTheme({
        ...consult,
        themeKey: matchedTheme[0].key!,
      });
    }
    const topicsFromAI = allTopicsTags?.tags?.filter((topic) =>
      response.topics?.includes(topic.name!),
    );
    setSelectedTopics(topicsFromAI);
    await saveTags({
      tags: topicsFromAI?.map((tag) => tag.name!) ?? [],
      id: consult.id,
    });

    sessionDiscussedRef.current?.setMarkdown(response.sessionNotes ?? "");

    appInsights.trackEvent({
      name: "ExpertsFlow",
      properties: {
        flow: "session-notes",
        eventType: "generate-with-ai",
        entityId: consult.id.toString(),
      },
    });
    setIsTranscriptionSummaryLoading(false);
  };

  const filteredAndSortedThemes = useMemo(
    () =>
      allSessionThemes?.toSorted((a, b) =>
        (
          getTranslatedThemeNameInSessionLanguage(
            a.key!,
            consult.consultLanguage,
          ) ?? ""
        ).localeCompare(
          getTranslatedThemeNameInSessionLanguage(
            b.key,
            consult.consultLanguage,
          ) ?? "",
        ),
      ),
    [allSessionThemes, consult.consultLanguage],
  );

  const externalHealthProviders = useMemo(
    () =>
      consult.consultType === SESSION_TYPE.FINANCIAL_WELLBEING
        ? getFinancialWellbeingExternalHealthProviders(t)
        : getExternalHealthProviders(t),
    [consult.consultType, t],
  );

  const helpNeededOptions = useMemo(
    () =>
      consult.consultType === SESSION_TYPE.FINANCIAL_WELLBEING
        ? getFinancialWellbeingHelpNeededOptions(t)
        : getHelpNeededOptions(t),
    [consult.consultType, t],
  );

  const getExternalHealthProvider = () => {
    if (!notes.provider) return emptyState;
    return (
      externalHealthProviders.find((p) => p.value === notes.provider)?.label ??
      notes.provider
    );
  };

  const getHelpNeededText = () => {
    if (!notes.helpNeeded) {
      return emptyState;
    }

    return (
      helpNeededOptions.find((option) => option.value === notes.helpNeeded)
        ?.label ?? notes.helpNeeded
    );
  };

  const getNextStepsText = () => {
    const nextSteps = getNextStepsForSessionNotes(t);
    return (
      nextSteps.find((s) => s.value === notes.nextSteps)?.label ?? emptyState
    );
  };

  const { mutateAsync: updateOrCreateNote, isPending: updateNoteSubmiting } =
    useConsultNotesMutation(consult.user.id!, consult.id);
  const { mutateAsync: deleteNotes } = useDeleteSessionNoteMutation(
    consult.user.id!,
    consult.id,
  );
  const reasonBookRef = useRef<MDXEditorMethods>(null);
  const handleSectionChange = useCallback(
    (section: NoteSections | undefined) => {
      if (
        editingSection === NoteSections.SESSION_DISCUSSED ||
        editingSection === NoteSections.REASON_BOOK
      )
        return;
      setEditingSection(section);
    },
    [editingSection],
  );

  const deleteDossierNoteIfExists = useCallback(
    async (questions: string[]) => {
      const existingNotes = consult.dossierNotes?.filter(
        (n) => n.question != null && questions.includes(n.question),
      );
      if (existingNotes && existingNotes.length > 0) {
        await deleteNotes(questions);
      }
    },
    [consult.dossierNotes, deleteNotes],
  );

  const handleHelpNeededSave = useCallback(
    async (newVal: string) => {
      notes.helpNeeded = newVal;
      await updateOrCreateNote({
        question: SESSION_NOTES_SECTIONS.HELP_NEEDED,
        answer: newVal,
      });
      if (newVal !== HELP_NEEDED_VALUES.NO_REFER_TO_OTHER_PROVIDER) {
        await deleteDossierNoteIfExists([
          SESSION_NOTES_SECTIONS.PROVIDER,
          SESSION_NOTES_SECTIONS.PROVIDER_NAME,
        ]);
      }
    },
    [notes, updateOrCreateNote, deleteDossierNoteIfExists],
  );

  const handleProviderSave = useCallback(
    async (newVal: string) => {
      notes.provider = newVal;
      await updateOrCreateNote({
        question: SESSION_NOTES_SECTIONS.PROVIDER,
        answer: newVal,
      });
      if (newVal !== PROVIDER_VALUES.OTHER) {
        await deleteDossierNoteIfExists([SESSION_NOTES_SECTIONS.PROVIDER_NAME]);
      }
    },
    [notes, updateOrCreateNote, deleteDossierNoteIfExists],
  );

  const contextValue = useMemo(
    () => ({
      editingSection,
      handleSectionChange,
      closeEditing: () => setEditingSection(undefined),
    }),
    [editingSection, handleSectionChange],
  );
  return (
    <EditingContext.Provider value={contextValue}>
      <div
        className="p-6 w-full md:mb-4 bg-white rounded-lg shadow-md flex flex-col"
        data-cy="session-notes"
      >
        <div className="flex justify-between items-center">
          <div className="sub1 mb-4">Session Information</div>
          {consult.hasTranscription && (
            <div className="flex gap-2 p-2">
              <Button
                loading={isTranscriptionSummaryLoading}
                onClick={loadTranscriptionSummary}
                className="!px-4 !py-1"
              >
                Autofill with AI
              </Button>
            </div>
          )}
        </div>

        {consultIsLoading ? (
          <div className="flex w-full p-8 justify-center">
            <Spinner />
          </div>
        ) : (
          <>
            <div className="flex flex-col mt-4 mb-10">
              {/* Theme */}
              <SessionNoteRow label="Theme of session">
                {editingSection !== NoteSections.THEME ? (
                  <EditableDisplay
                    className={CLICKABLE_FIELD_CLASS}
                    onActivate={() => handleSectionChange(NoteSections.THEME)}
                  >
                    {getTranslatedThemeNameInSessionLanguage(
                      notes.theme?.key ?? "",
                      consult.consultLanguage,
                    ) ?? emptyState}
                  </EditableDisplay>
                ) : (
                  <Autocomplete
                    openOnFocus
                    autoFocus
                    disablePortal
                    options={filteredAndSortedThemes}
                    getOptionLabel={(opt) =>
                      getTranslatedThemeNameInSessionLanguage(
                        opt.key,
                        consult.consultLanguage,
                      )
                    }
                    value={notes.theme ?? null}
                    className="w-full"
                    onChange={async (_e, newVal) => {
                      notes.theme = newVal ?? undefined;
                      if (newVal)
                        await updateConsultTheme(
                          {
                            ...consult,
                            themeKey: newVal.key,
                          },
                          {
                            onError: () => {
                              snackbarShowMessage(
                                "Failed to update theme",
                                "error",
                              );
                            },
                          },
                        );
                      handleSectionChange(undefined);
                    }}
                    renderInput={(params) => (
                      <TextField autoFocus variant="standard" {...params} />
                    )}
                  />
                )}
              </SessionNoteRow>

              {/* Topics */}
              <SessionNoteRow label="Topics covered">
                {editingSection !== NoteSections.TOPICS ? (
                  <EditableDisplay
                    className={CLICKABLE_FIELD_CLASS}
                    onActivate={() => handleSectionChange(NoteSections.TOPICS)}
                  >
                    {selectedTopics && selectedTopics.length > 0
                      ? selectedTopics
                          ?.map((topic) =>
                            getTranslatedTopicName(
                              topic.name,
                              consult.consultLanguage,
                            ),
                          )
                          .join(", ")
                      : emptyState}
                  </EditableDisplay>
                ) : (
                  <Autocomplete
                    openOnFocus
                    disablePortal
                    multiple
                    disableCloseOnSelect
                    options={sortedTopics}
                    getOptionLabel={(opt) =>
                      getTranslatedTopicName(opt.name, consult.consultLanguage)
                    }
                    value={selectedTopics ?? []}
                    className="w-full"
                    onChange={(_e, newVal, reason) => {
                      if (
                        ["selectOption", "removeOption", "clear"].includes(
                          reason,
                        )
                      ) {
                        setSelectedTopics(newVal as ModelsTag[]);
                      }
                    }}
                    onClose={async () => {
                      await saveTags(
                        {
                          tags: selectedTopics?.map((tag) => tag.name!) ?? [],
                          id: consult.id,
                        },
                        {
                          onError: () => {
                            snackbarShowMessage(
                              "Failed to update topics",
                              "error",
                            );
                          },
                        },
                      );
                      handleSectionChange(undefined);
                    }}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon className="mr-3" />}
                          checkedIcon={<CheckBoxIcon className="mr-3" />}
                          className="text-indigo-800 body1"
                          checked={selected}
                        />
                        {getTranslatedTopicName(
                          option.name,
                          consult.consultLanguage,
                        )}
                      </li>
                    )}
                    renderTags={(tags) =>
                      tags.map((tag, idx) => (
                        <p key={tag.name} className="pl-1">
                          {getTranslatedTopicName(
                            tag.name,
                            consult.consultLanguage,
                          )}
                          {idx < tags.length - 1 && ","}
                        </p>
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        autoFocus
                        label={t("Consults.SessionDetails.SelectTopics")}
                        variant="standard"
                      />
                    )}
                  />
                )}
              </SessionNoteRow>

              {/* Referred */}
              <SessionNoteRow label="Referred">
                <EditableSelectField<string>
                  data-testid="helpNeeded-select"
                  section={NoteSections.HELP_NEEDED}
                  currentValue={notes.helpNeeded}
                  displayValue={getHelpNeededText()}
                  options={helpNeededOptions.map((option) => ({
                    label: option.label,
                    value: option.value,
                  }))}
                  onSave={handleHelpNeededSave}
                />
              </SessionNoteRow>

              {/* Other health provider */}
              {notes.helpNeeded ===
                HELP_NEEDED_VALUES.NO_REFER_TO_OTHER_PROVIDER && (
                <SessionNoteRow label="Other health provider">
                  <EditableSelectField<string>
                    data-testid="provider-select"
                    section={NoteSections.PROVIDER}
                    currentValue={notes.provider}
                    displayValue={getExternalHealthProvider()}
                    options={externalHealthProviders.map((p) => ({
                      label: p.label,
                      value: p.value,
                    }))}
                    onSave={handleProviderSave}
                  />
                </SessionNoteRow>
              )}

              {/* Other, namely */}
              {notes.helpNeeded ===
                HELP_NEEDED_VALUES.NO_REFER_TO_OTHER_PROVIDER &&
                notes.provider === PROVIDER_VALUES.OTHER && (
                  <SessionNoteRow label="">
                    {editingSection !== NoteSections.PROVIDER_NAME ? (
                      <EditableDisplay
                        data-testid="custom-provider-name-field"
                        className={CLICKABLE_FIELD_CLASS}
                        onActivate={() =>
                          handleSectionChange(NoteSections.PROVIDER_NAME)
                        }
                      >
                        {notes.providerName || emptyState}
                      </EditableDisplay>
                    ) : (
                      <TextField
                        autoFocus
                        variant="standard"
                        defaultValue={notes.providerName || ""}
                        placeholder="Enter provider name"
                        className="w-full"
                        onBlur={async (e) => {
                          const newValue = e.target.value;
                          notes.providerName = newValue;
                          await updateOrCreateNote({
                            question: SESSION_NOTES_SECTIONS.PROVIDER_NAME,
                            answer: newValue,
                          });
                          handleSectionChange(undefined);
                        }}
                        onKeyDown={async (e) => {
                          if (e.key === "Enter") {
                            const newValue = (e.target as HTMLInputElement)
                              .value;
                            notes.providerName = newValue;
                            await updateOrCreateNote({
                              question: SESSION_NOTES_SECTIONS.PROVIDER_NAME,
                              answer: newValue,
                            });
                            handleSectionChange(undefined);
                          }
                          if (e.key === "Escape") {
                            handleSectionChange(undefined);
                          }
                        }}
                      />
                    )}
                  </SessionNoteRow>
                )}

              {/* Follow-up */}
              <SessionNoteRow label="Follow-up">
                <EditableSelectField<string>
                  section={NoteSections.NEXT_STEPS}
                  currentValue={notes.nextSteps}
                  displayValue={getNextStepsText()}
                  options={getNextStepsForSessionNotes(t).map((step) => ({
                    label: step.label,
                    value: step.value,
                  }))}
                  onSave={async (newVal) => {
                    notes.nextSteps = newVal;
                    await updateOrCreateNote({
                      question: SESSION_NOTES_SECTIONS.NEXT_STEPS,
                      answer: newVal,
                    });
                  }}
                />
              </SessionNoteRow>
            </div>

            {/* Background of session */}
            <div className="flex flex-col mt-4 mb-10">
              <div className="sub1 m-1">Background of session</div>
              <EditableMarkdownSection
                section={NoteSections.REASON_BOOK}
                currentValue={notes.reasonBook}
                editorRef={reasonBookRef}
                onSave={async (content) => {
                  notes.reasonBook = content;
                  await updateOrCreateNote({
                    question: SESSION_NOTES_SECTIONS.REASON_BOOK,
                    answer: content,
                  });
                }}
                isSubmitting={updateNoteSubmiting}
              />
            </div>

            {/* Discussed during the session */}
            <div className="flex flex-col mt-4 mb-10">
              <div className="sub1 m-1">Discussed during the session</div>
              <EditableMarkdownSection
                section={NoteSections.SESSION_DISCUSSED}
                currentValue={notes.sessionDiscussed}
                editorRef={sessionDiscussedRef}
                onSave={async (content) => {
                  notes.sessionDiscussed = content;
                  await updateOrCreateNote({
                    question: SESSION_NOTES_SECTIONS.SESSION_DISCUSSED,
                    answer: content,
                  });
                }}
                isSubmitting={updateNoteSubmiting}
              />
            </div>

            {/* Attachments */}
            <div className="flex flex-col mt-4 mb-10">
              <AttachmentsBox consultId={consult.id} />
            </div>
          </>
        )}
      </div>
    </EditingContext.Provider>
  );
};

export default withSnackbar(SessionNotesPaper);
